import { BUCKETS, catToBucket } from '../types/cost.js';
import { monthLabel } from './dates.js';
const maandVan = (l) => l.maand;
const dagVan = (l) => Number(l.datum?.slice(8, 10) ?? 0);
/** De maand ervóór, als `YYYY-MM`. */
export function vorigeMaand(maand) {
    const [j, m] = maand.split('-').map(Number);
    if (!j || !m)
        return maand;
    return m === 1 ? `${j - 1}-12` : `${j}-${String(m - 1).padStart(2, '0')}`;
}
/**
 * Splitst de regels in "deze periode" en "dezelfde spanne ervóór".
 *
 * Bij een maand telt de **dag** mee. De tankpasfactuur stopt midden in de maand,
 * dus een lopende maand hoort dag 1 tot N tegen dag 1 tot N van de maand ervoor
 * te leggen. Doe je dat niet, dan vergelijk je drie weken met een volle maand en
 * kan het teken omklappen: op de vlootcijfers las de naïeve vergelijking −30,4%
 * waar de eerlijke +13,1% zei. Zie dezelfde afweging in `tracker.ts`.
 *
 * `dagGrens` is de laatste dag waarop er gegevens zijn. Geef je hem niet mee, dan
 * wordt het de laatste dag die in de gekozen maand voorkomt.
 */
export function kiesPeriode(regels, filter, dagGrens) {
    if (filter === null) {
        return {
            filter,
            label: 'hele periode',
            regels: [...regels],
            vorige: [],
            vorigeLabel: null,
        };
    }
    if (filter === 'YTD') {
        const jaren = [...new Set(regels.map((l) => maandVan(l).slice(0, 4)))].sort();
        const jaar = jaren[jaren.length - 1];
        if (!jaar)
            return { filter, label: 'dit jaar', regels: [], vorige: [], vorigeLabel: null };
        const vorigJaar = String(Number(jaar) - 1);
        // Tot dezelfde maand vorig jaar, anders leg je een half jaar naast een heel.
        const totMaand = Math.max(...regels.filter((l) => l.maand.startsWith(jaar)).map((l) => Number(l.maand.slice(5, 7))));
        return {
            filter,
            label: `dit jaar (${jaar})`,
            regels: regels.filter((l) => l.maand.startsWith(`${jaar}-`)),
            vorige: regels.filter((l) => l.maand.startsWith(`${vorigJaar}-`) && Number(l.maand.slice(5, 7)) <= totMaand),
            vorigeLabel: vorigJaar,
        };
    }
    const inMaand = regels.filter((l) => l.maand === filter);
    const grens = dagGrens ?? Math.max(0, ...inMaand.map(dagVan));
    const vorig = vorigeMaand(filter);
    return {
        filter,
        label: monthLabel(filter),
        regels: inMaand,
        // Dezelfde dagen van de maand ervoor; zonder datum telt een regel niet mee,
        // want dan valt niet te zeggen of hij binnen de spanne viel.
        vorige: regels.filter((l) => l.maand === vorig && dagVan(l) > 0 && dagVan(l) <= grens),
        vorigeLabel: monthLabel(vorig),
    };
}
/** Per kostenpost, alleen de posten waar iets in zit, grootste eerst. */
export function kostenPerBucket(regels) {
    const bedrag = new Map();
    const aantal = new Map();
    for (const l of regels) {
        const b = catToBucket(l.categorie);
        bedrag.set(b, (bedrag.get(b) ?? 0) + l.bedragExcl);
        aantal.set(b, (aantal.get(b) ?? 0) + 1);
    }
    return BUCKETS.filter((b) => (bedrag.get(b.key) ?? 0) !== 0)
        .map((b) => ({
        key: b.key,
        label: b.label,
        color: b.color,
        bedrag: bedrag.get(b.key) ?? 0,
        aantal: aantal.get(b.key) ?? 0,
    }))
        .sort((a, b) => b.bedrag - a.bedrag);
}
/**
 * Per maand een gestapelde staaf. Maanden zonder transacties houden hun plek,
 * anders schuift de as dicht en verdwijnt juist de maand waarin niets gebeurde.
 */
export function kostenPerMaand(regels, maanden) {
    return maanden.map((maand) => {
        const inMaand = regels.filter((l) => l.maand === maand);
        const delen = kostenPerBucket(inMaand).map((b) => ({
            key: b.key,
            color: b.color,
            bedrag: b.bedrag,
        }));
        return {
            maand,
            label: monthLabel(maand),
            totaal: inMaand.reduce((s, l) => s + l.bedragExcl, 0),
            delen,
        };
    });
}
/** Groepeert op een vrij veld (categorie, station, kenteken), grootste eerst. */
export function groepeerKosten(regels, sleutel, leeg = 'Onbekend') {
    const bedrag = new Map();
    const aantal = new Map();
    for (const l of regels) {
        const k = sleutel(l)?.trim() || leeg;
        bedrag.set(k, (bedrag.get(k) ?? 0) + l.bedragExcl);
        aantal.set(k, (aantal.get(k) ?? 0) + 1);
    }
    return [...bedrag.entries()]
        .map(([naam, b]) => ({ naam, bedrag: b, aantal: aantal.get(naam) ?? 0 }))
        .sort((a, b) => b.bedrag - a.bedrag);
}
/** Telt liters of kWh op, met de gemiddelde prijs die er werkelijk voor betaald is. */
export function verbruik(regels, eenheid) {
    let waarde = 0;
    let betaald = 0;
    let aantal = 0;
    for (const l of regels) {
        if (!l.hoeveelheid || !l.eenheid)
            continue;
        const u = l.eenheid.toUpperCase();
        const past = eenheid === 'LTR' ? u.startsWith('LTR') || u.startsWith('LITER') : u.startsWith('KWH');
        if (!past)
            continue;
        waarde += l.hoeveelheid;
        betaald += l.bedragExcl;
        aantal += 1;
    }
    // Een gemiddelde prijs uit de losse `prijs`-velden zou elke tankbeurt even zwaar
    // laten wegen; door te delen weegt een volle tank zwaarder dan vijf liter.
    return { waarde, prijs: waarde > 0 ? betaald / waarde : null, aantal };
}
/**
 * Gereden kilometers: per auto de spanne tussen de laagste en de hoogste
 * betrouwbare tellerstand, bij elkaar opgeteld.
 *
 * Alleen standen uit de transacties zelf, dus een pas die alleen heeft geladen
 * levert niets op: een laadpaal vraagt de kilometerstand niet. Dezelfde
 * berekening staat op het pasdetail.
 */
export function geredenKm(regels) {
    const perAuto = new Map();
    for (const l of regels) {
        if (l.kmStand == null || l.kmStand <= 0)
            continue;
        (perAuto.get(l.kenteken) ?? perAuto.set(l.kenteken, []).get(l.kenteken)).push(l.kmStand);
    }
    let totaal = 0;
    let gevonden = false;
    for (const standen of perAuto.values()) {
        if (standen.length < 2)
            continue;
        totaal += Math.max(...standen) - Math.min(...standen);
        gevonden = true;
    }
    return gevonden ? totaal : null;
}
export const kostenTotaal = (regels) => regels.reduce((s, l) => s + l.bedragExcl, 0);
//# sourceMappingURL=persoonkosten.js.map